// iter148.2 — Sık Beraber Alınanlar (Frequently Bought Together)
// Ürün detayında ana ürün + 2-3 önerilen ürünü aynı sepete ekleme bandı.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { fmtTRY } from "../../lib/format";
import { useCart } from "../../context/CartContext";
import { toast } from "sonner";
import { Package, Plus, ShoppingCart, Sparkles, TrendingUp } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function FrequentlyBoughtTogether({ mainProduct, brandPrimary = "#8D1B3D" }) {
  const { add } = useCart();
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState({});    // { productId: true|false }
  const [source, setSource] = useState("");        // co_purchase | category

  useEffect(() => {
    if (!mainProduct?.id) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/shop/products/${mainProduct.id}/frequently-bought-together?limit=3`);
        setItems(data.items || []);
        setSource(data.source || "");
        // Varsayılan: hepsi seçili
        const sel = {};
        (data.items || []).forEach((it) => { sel[it.id] = true; });
        setSelected(sel);
      } catch { setItems([]); }
    })();
  }, [mainProduct?.id]);

  if (!items || items.length === 0) return null;

  const mainPrice = mainProduct?.price ?? mainProduct?.unit_price ?? 0;
  const mainImg = (Array.isArray(mainProduct?.images) && mainProduct.images[0]) || mainProduct?.image || "";

  const selectedItems = items.filter((it) => selected[it.id]);
  const totalPrice = mainPrice + selectedItems.reduce((sum, it) => sum + (it.price || 0), 0);
  const totalCount = 1 + selectedItems.length;

  const toggle = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const addAll = () => {
    // Ana ürün + seçili ürünleri sepete ekle
    add({ ...mainProduct, unit_price: mainPrice }, 1);
    selectedItems.forEach((it) => {
      add({
        id: it.id, name: it.name, sku: it.sku, image: it.image,
        brand_name: it.brand_name, unit_price: it.price || 0, price: it.price || 0,
      }, 1);
    });
    toast.success(`${totalCount} ürün sepete eklendi (${fmtTRY(totalPrice)})`);
  };

  return (
    <section className="mt-10 border-t border-zinc-200 pt-8" data-testid="fbt-section">
      <div className="mb-5">
        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold mb-1" style={{ color: brandPrimary }}>
          {source === "co_purchase"
            ? <><TrendingUp size={12}/> Bu Ürünle Sık Alınanlar</>
            : <><Sparkles size={12}/> Beraber İyi Gider</>}
        </div>
        <h2 className="font-display font-bold text-2xl text-zinc-900">Sık Beraber Alınanlar</h2>
        <p className="text-sm text-zinc-500 mt-1">
          {source === "co_purchase"
            ? "Diğer müşterilerimizin bu ürünle beraber tercih ettiği tamamlayıcı ürünler."
            : "Bu ürünle uyumlu, aynı kategorideki önerilerimiz."}
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Sol: ürün zinciri (ana + öneriler) */}
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 flex-wrap" data-testid="fbt-chain">
            {/* Ana ürün — non-removable */}
            <div className="relative border border-primary/40 rounded-lg overflow-hidden bg-primary/5 flex-1 min-w-[130px] max-w-[180px]" data-testid="fbt-main">
              <div className="aspect-square bg-zinc-50 overflow-hidden">
                {mainImg ? <img src={mainImg} alt={mainProduct.name} className="w-full h-full object-cover"/> :
                  <div className="w-full h-full flex items-center justify-center"><Package size={30} className="text-zinc-300"/></div>}
              </div>
              <div className="p-2">
                <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Bu Ürün</div>
                <div className="text-[11px] font-medium line-clamp-2 text-zinc-800">{mainProduct.name}</div>
                {mainPrice > 0 && <div className="text-xs font-bold mt-0.5 tabular-nums" style={{ color: brandPrimary }}>{fmtTRY(mainPrice)}</div>}
              </div>
            </div>
            {items.map((it) => (
              <div key={it.id} className="flex items-center gap-2">
                <Plus size={18} className="text-zinc-400 shrink-0"/>
                <div className={`relative border rounded-lg overflow-hidden flex-1 min-w-[130px] max-w-[180px] transition-all ${selected[it.id] ? "border-primary/40 bg-white shadow-sm" : "border-zinc-200 bg-zinc-50 opacity-60"}`} data-testid={`fbt-item-${it.id}`}>
                  <label className="absolute top-1.5 left-1.5 z-10 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!selected[it.id]}
                      onChange={() => toggle(it.id)}
                      className="w-4 h-4 rounded border-zinc-300 text-primary focus:ring-primary/30"
                      data-testid={`fbt-check-${it.id}`}
                    />
                  </label>
                  <Link to={`/urun/${it.sku || it.id}`} className="block">
                    <div className="aspect-square bg-zinc-50 overflow-hidden">
                      {it.image ? <img src={it.image} alt={it.name} className="w-full h-full object-cover hover:scale-105 transition-transform"/> :
                        <div className="w-full h-full flex items-center justify-center"><Package size={30} className="text-zinc-300"/></div>}
                    </div>
                    <div className="p-2">
                      <div className="text-[10px] text-zinc-500 uppercase truncate">{it.brand_name || "—"}</div>
                      <div className="text-[11px] font-medium line-clamp-2 text-zinc-800">{it.name}</div>
                      {it.price > 0 && <div className="text-xs font-bold mt-0.5 tabular-nums" style={{ color: brandPrimary }}>{fmtTRY(it.price)}</div>}
                    </div>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sağ: toplam + hepsini ekle */}
        <div className="md:col-span-1">
          <div className="bg-gradient-to-br from-primary/5 to-white border border-primary/20 rounded-xl p-5 sticky top-24" data-testid="fbt-summary">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">
              {totalCount} Ürünlük Paket
            </div>
            <div className="text-xs text-zinc-600 mb-1">Toplam tutar</div>
            <div className="text-3xl font-display font-bold tabular-nums mb-1" style={{ color: brandPrimary }} data-testid="fbt-total">
              {fmtTRY(totalPrice)}
            </div>
            <div className="text-[11px] text-zinc-500 mb-4">KDV Dahil · Kargo hariç</div>
            <button
              type="button"
              onClick={addAll}
              disabled={selectedItems.length === 0}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-white font-semibold text-sm shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              style={{ background: brandPrimary }}
              data-testid="fbt-add-all"
            >
              <ShoppingCart size={16}/> Hepsini Sepete Ekle
            </button>
            <p className="text-[10px] text-zinc-400 mt-2 leading-relaxed">
              İşaretini kaldırarak istediğin ürünleri sepetten çıkarabilirsin. Ana ürün her zaman dahildir.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
